import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import UploadForm from '../components/marketing/UploadForm';
import UploadDropzone from '../components/marketing/UploadDropzone';
import AssetCard from '../components/marketing/AssetCard';
import PlannerGrid from '../components/marketing/PlannerGrid';
import CampaignCalendar from '../components/marketing/CampaignCalendar';
import AssetDetailModal from '../components/marketing/AssetDetailModal';
import {
  CalendarIcon,
  GridIcon,
  PlusCircleIcon,
  SearchIcon,
  SparkleIcon,
} from '../components/icons';
import { useAuth } from '../context/AuthContext';
import {
  addAsset,
  deleteAsset,
  latestVersion,
  listAssets,
  listCommentCounts,
  MAX_PINNED,
  setAssetStatus,
  setPinned,
  STATUSES,
} from '../lib/marketing';
import { disconnectInstagram, getInstagramMedia, startInstagramConnect } from '../lib/instagram';
import './Marketing.css';
import { useConfirm } from '../components/ConfirmDialog';

const STATUS_FILTERS = ['All', ...STATUSES];
const VIEWS = [
  { id: 'Planner', label: 'Planner', Icon: GridIcon },
  { id: 'Calendar', label: 'Calendar', Icon: CalendarIcon },
];
const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'oldest', label: 'Oldest' },
];

export default function Marketing() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [commentCounts, setCommentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pinError, setPinError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [formOpen, setFormOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);
  const [view, setView] = useState(VIEWS[0].id);
  const [openAssetId, setOpenAssetId] = useState(null);
  const [instagram, setInstagram] = useState({ connected: false, username: null });
  const [igMessage, setIgMessage] = useState(null);
  const [liveMedia, setLiveMedia] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, counts] = await Promise.all([listAssets(), listCommentCounts()]);
      setAssets(rows);
      setCommentCounts(counts);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // The live feed is fetched separately and its failure is contained: not
  // being connected to Instagram (or the Edge Function being down) must not
  // stop you planning content.
  const loadLive = useCallback(async () => {
    try {
      const body = await getInstagramMedia();
      setInstagram({ connected: body.connected, username: body.username || null });
      setLiveMedia(body.media || []);
      // Connected, but Instagram refused the media request — say so rather
      // than silently showing an empty grid.
      if (body.connected && body.error) {
        setIgMessage({
          ok: false,
          text: `Connected as @${body.username}, but Instagram refused the request: ${body.error}`,
        });
      }
    } catch (err) {
      // A thrown call means the function itself failed. That is NOT the same
      // as being disconnected, and reporting it as such is what hid a working
      // connection behind a permanent "Not connected".
      setInstagram({ connected: false, username: null });
      setLiveMedia([]);
      setIgMessage({ ok: false, text: `Couldn't reach Instagram: ${err.message}` });
    }
  }, []);

  useEffect(() => {
    loadLive();
  }, [loadLive]);

  // The Edge Function bounces back here after the OAuth hop, carrying either
  // ig_connected=<username> or ig_error=<reason>. The reason used to be
  // stripped from the URL and dropped on the floor, so a failed connection
  // looked identical to nothing happening — you'd be sent back to a page that
  // still said "Not connected", with no way to find out why.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('ig_connected');
    const failed = params.get('ig_error');
    if (connected || failed) {
      setIgMessage(
        connected
          ? { ok: true, text: `Connected to @${connected}.` }
          : { ok: false, text: `Instagram didn't connect: ${failed}` }
      );
      window.history.replaceState({}, '', window.location.pathname);
      loadLive();
    }
  }, [loadLive]);

  const handleDisconnectInstagram = async () => {
    await disconnectInstagram();
    await loadLive();
  };

  const handleDroppedFile = (file) => {
    setDroppedFile(file);
    setFormOpen(true);
  };

  const handleUpload = async (fields, file) => {
    await addAsset({ ...fields, created_by: user.id }, file);
    await loadAll();
    setFormOpen(false);
    setDroppedFile(null);
  };

  const handleSetStatus = async (id, status) => {
    await setAssetStatus(id, status);
    await loadAll();
  };

  const handleSetPinned = async (id, pinned) => {
    setPinError(null);
    if (pinned && assets.filter((a) => a.pinned_at).length >= MAX_PINNED) {
      setPinError(`You can only pin up to ${MAX_PINNED} posts — unpin one first.`);
      return;
    }
    await setPinned(id, pinned);
    await loadAll();
  };

  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Delete this asset?',
      body: "Every uploaded version and comment goes with it.",
    }))) return;

    await deleteAsset(id);
    await loadAll();
  };

  const openAsset = assets.find((a) => a.id === openAssetId) || null;

  // Counts reflect the whole library, not the active filter — a pill reading
  // "0" only because its own filter is applied would be nonsense.
  const statusCounts = STATUS_FILTERS.reduce((acc, s) => {
    acc[s] = s === 'All' ? assets.length : assets.filter((a) => a.status === s).length;
    return acc;
  }, {});

  const filteredAssets = assets
    .filter((a) => {
      if (statusFilter !== 'All' && a.status !== statusFilter) return false;
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;
      const caption = latestVersion(a)?.caption || '';
      return `${caption} ${a.channel}`.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      const at = new Date(latestVersion(a)?.uploaded_at || a.created_at);
      const bt = new Date(latestVersion(b)?.uploaded_at || b.created_at);
      return sort === 'newest' ? bt - at : at - bt;
    });

  return (
    <>
      <PageHeader
        title={
          <>
            Marketing
            <SparkleIcon className="page-title-sparkle" />
          </>
        }
        subtitle="Plan, review and schedule your social content."
        actions={
          <button className="primary-action" onClick={() => setFormOpen(true)}>
            <PlusCircleIcon />
            Upload Campaign Asset
          </button>
        }
      />

      {igMessage && (
        <div className={'ig-banner' + (igMessage.ok ? ' ok' : ' error')} role="status">
          <span>{igMessage.text}</span>
          <button type="button" onClick={() => setIgMessage(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <nav className="view-tabs">
        {VIEWS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={'view-tab' + (view === id ? ' active' : '')}
            onClick={() => setView(id)}
          >
            <Icon />
            {label}
          </button>
        ))}
      </nav>

      {loadError && <p className="marketing-error">Couldn't load data: {loadError}</p>}
      {pinError && <p className="marketing-error">{pinError}</p>}

      {view === 'Calendar' ? (
        <section className="panel">
          <CampaignCalendar />
        </section>
      ) : (
        <div className="marketing-planner">
          <section className="panel library-panel">
            <div className="library-toolbar">
              <div className="library-search">
                <SearchIcon />
                <input
                  type="search"
                  placeholder="Search campaign assets…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                className="library-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    Status: {s}
                  </option>
                ))}
              </select>
              <select
                className="library-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Sort assets"
              >
                {SORTS.map((s) => (
                  <option key={s.id} value={s.id}>
                    Sort: {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="library-pills">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  className={'library-pill' + (statusFilter === s ? ' active' : '')}
                  onClick={() => setStatusFilter(s)}
                >
                  {s}
                  <span className="library-pill-count">{statusCounts[s] ?? 0}</span>
                </button>
              ))}
            </div>

            {formOpen && (
              <UploadForm
                initialFile={droppedFile}
                onSubmit={handleUpload}
                onCancel={() => {
                  setFormOpen(false);
                  setDroppedFile(null);
                }}
              />
            )}

            {loading ? (
              <p className="marketing-loading">Loading…</p>
            ) : (
              <>
                {filteredAssets.length > 0 && (
                  <div className="library-grid">
                    {filteredAssets.map((asset) => (
                      <AssetCard
                        key={asset.id}
                        asset={asset}
                        commentCount={commentCounts[asset.id] || 0}
                        onOpen={() => setOpenAssetId(asset.id)}
                      />
                    ))}
                  </div>
                )}

                {filteredAssets.length === 0 && assets.length > 0 && (
                  <p className="marketing-empty-inline">No assets match this filter.</p>
                )}

                {/* The dropzone doubles as the empty state — an empty library
                    should still offer the next action, not an apology. */}
                <UploadDropzone onFiles={handleDroppedFile} empty={assets.length === 0} />
              </>
            )}
          </section>

          <PlannerGrid
            assets={assets}
            liveMedia={liveMedia}
            instagram={instagram}
            onReordered={loadAll}
            onConnect={startInstagramConnect}
            onDisconnect={handleDisconnectInstagram}
            onRefresh={loadLive}
            onViewCalendar={() => setView('Calendar')}
          />
        </div>
      )}

      {/* Resolved from `assets` so the dialog re-renders with fresh data
          after any mutation refetches. */}
      {openAsset && (
        <AssetDetailModal
          asset={openAsset}
          onClose={() => setOpenAssetId(null)}
          onSetStatus={handleSetStatus}
          onSetPinned={handleSetPinned}
          onDelete={handleDelete}
          onChanged={loadAll}
        />
      )}
    </>
  );
}
